/*global swal*/
import React, { useState } from 'react';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { differenceInCalendarYears, parseISO } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import cn from 'classnames';
import SweetAlert from 'react-swal';
import { Link } from 'react-router-dom';

import { short } from '../../common/formatting/date';
import { formatCurrency } from '../../common/formatting/number';
import { fullname } from '../../common/formatting/provider';
import { EVISIT_STATUS } from '../../../../config/constants';
import { getCurrentProvider } from '../../../../lib/util';

import MissingItemAlert from './components/MissingItemAlert';
import ProfilePic from './components/ProfilePic';
import { Button, DropdownButton } from '../../../../components';
import isString from 'lodash.isstring';

const PatientBar = ({
	user,
	evisit,
	isPatientConnected = false,
	onReinvitePatient,
	onStartVisit,
	onChangeFee,
	onCancelVisit,
	onEndVisit,
	onReferVisit,
	isDependentEvisit,
	isUpdating,
}) => {
	const [showStartSwal, setShowStartSwal] = useState(false);
	const [showEndSwal, setShowEndSwal] = useState(false);
	const [showCancelSwal, setShowCancelSwal] = useState(false);

	// const careteam = [];

	let /*insurance,*/ docName;
	const patient = evisit.patient;
	const currentStatus = evisit.evisit_status;
	const isClosed = Boolean(evisit.end_dttm);
	const isMissingGender = !patient.gender;
	const isAddressIncomplete =
		isEmpty(patient.address) ||
		isEmpty(patient.city) ||
		isEmpty(patient.state) ||
		isEmpty(patient.zip);
	// const isMissingPharmacy = !patient.eazyscripts_pharmacy_id;
	const isIncomplete = isMissingGender || isAddressIncomplete; // || isMissingPharmacy);
	const isTAV =
		evisit.provider_visit_type && evisit.provider_visit_type.instant_visit_ind;
	const isOpen = currentStatus === EVISIT_STATUS.IN_PROGRESS;
	const isCancelable = !isOpen && !isClosed;
	// const canRx = !isMissingGender && !isAddressIncomplete /* && !isMissingPharmacy */ && user.get('eazyscripts_ind');
	// const pharmacy = evisit.pharmacy;
	const currentProvider = getCurrentProvider(evisit);
	const myPatient = currentProvider?.providerId === user.get('id');
	const referral = evisit['evisit-referral'];
	const patient_dependent = evisit.patient_dependent;
	const dependentAge = isDependentEvisit
		? differenceInCalendarYears(
				new Date(),
				parseISO(patient_dependent.dependent_birth_dttm)
		  )
		: 0;
	const age = differenceInCalendarYears(
		new Date(),
		parseISO(patient.birth_dttm)
	);
	const isScheduled = [
		EVISIT_STATUS.SCHEDULED,
		EVISIT_STATUS.SCHEDULED_PENDING,
	].includes(currentStatus);

	const handleConfirmStartVisit = () => {
		setShowStartSwal(true);
	};

	const handleStartVisit = (isConfirm) => {
		if (isConfirm) {
			onStartVisit();
		}
		setShowStartSwal(false);
		swal.close();
	};

	const handleConfirmEndVisit = () => {
		setShowEndSwal(true);
	};

	const handleEndVisit = () => {
		onEndVisit();
		setShowEndSwal(false);
		swal.close();
	};

	const handleConfirmCancelVisit = () => {
		setShowCancelSwal(true);
	};

	const handleCancelVisit = (reason) => {
		if (isString(reason)) {
			onCancelVisit(reason);
		}
		setShowCancelSwal(false);
		swal.close();
	};

	// if (patient.insurances && Array.isArray(patient.insurances) && patient.insurances.length > 0) {
	// 	insurance = patient.insurances[0];
	// }

	if (!myPatient && currentProvider) {
		docName = fullname(currentProvider);
	}

	return (
		<div>
			{!isClosed && isIncomplete && (
				<MissingItemAlert
					isMissingGender={isMissingGender}
					isAddressIncomplete={isAddressIncomplete}
					// isMissingPharmacy={isMissingPharmacy}
				/>
			)}
			<SweetAlert
				isOpen={showStartSwal}
				type="warning"
				title="Are you sure?"
				text="Start Visit?"
				showCancelButton
				confirmButtonText="Yes"
				closeOnConfirm
				closeOnCancel
				confirmButtonColor="#DD6B55"
				callback={handleStartVisit}
			/>
			<SweetAlert
				isOpen={showEndSwal}
				type="warning"
				title="Are you sure?"
				text="You are about to end this visit."
				showCancelButton
				confirmButtonText="Yes"
				closeOnConfirm={false}
				showLoaderOnConfirm
				confirmButtonColor="#DD6B55"
				callback={handleEndVisit}
			/>
			<SweetAlert
				isOpen={showCancelSwal}
				type="input"
				title="Are you sure?"
				text="Why are you canceling this visit?"
				showCancelButton
				confirmButtonText="Yes"
				cancelButtonText="No"
				confirmButtonColor="#DD6B55"
				closeOnConfirm={false}
				closeOnCancel
				inputPlaceholder="Reason for canceling visit (optional)"
				showLoaderOnConfirm
				callback={handleCancelVisit}
			/>
			<div
				className={cn('row', 'patient-profile', {
					'bg-warning-light': currentStatus === EVISIT_STATUS.IN_REVIEW,
					'bg-danger-light': [
						EVISIT_STATUS.IMH_IMCOMPLETE,
						EVISIT_STATUS.READY_FOR_PROVIDER,
						EVISIT_STATUS.CREATED,
					].includes(currentStatus),
				})}
			>
				<div className="col-md-2">
					<ProfilePic
						onReinvitePatient={onReinvitePatient}
						isClosed={isClosed}
						isDependentEvisit={isDependentEvisit}
						isPatientConnected={isPatientConnected}
						isTAV={isTAV}
						patient={patient}
					/>
				</div>
				<div className="col-md-10">
					<h2>
						{isDependentEvisit ? (
							<React.Fragment>
								{patient_dependent.dependent_name}{' '}
								{patient_dependent.dependent_name_last}
								<small>
									(dependent of {patient.name_first} {patient.name_last})
								</small>
							</React.Fragment>
						) : (
							<React.Fragment>
								{patient.name_first} {patient.name_last}
							</React.Fragment>
						)}
					</h2>
					<div className="row">
						<div className="col-md-3">
							<strong>DOB:</strong>
							{isDependentEvisit ? (
								<React.Fragment>
									{' '}
									{short(patient_dependent.dependent_birth_dttm)} (
									{dependentAge})
								</React.Fragment>
							) : (
								<React.Fragment>
									{' '}
									{short(patient.birth_dttm)} ({age})
								</React.Fragment>
							)}
						</div>
						<div className="col-md-3">
							<strong>Phone:</strong> {evisit.phone_cell}
						</div>
						<div className="col-md-6">
							<strong>Address:</strong> {patient.address} {patient.city}{' '}
							{patient.state} {patient.zip}
						</div>
					</div>
					<div className="row">
						<div className="col-md-3">
							<strong>Gender:</strong>
							{isDependentEvisit ? (
								<React.Fragment>
									{!isEmpty(patient_dependent.dependent_gender) &&
										(patient_dependent.dependent_gender == 'M'
											? ' Male'
											: ' Female')}
									{isEmpty(patient_dependent.dependent_gender) && ' <Unknown>'}
								</React.Fragment>
							) : (
								<React.Fragment>
									{!isEmpty(patient.gender) &&
										(patient.gender == 'M' ? ' Male' : ' Female')}
									{isEmpty(patient.gender) && ' <Unknown>'}
								</React.Fragment>
							)}
						</div>
						<div className="col-md-3">
							<strong>Visit #</strong>
							{evisit.id}
						</div>
						<div className="col-md-3">
							<strong>Visit Fee:</strong> {formatCurrency(evisit.paymentAmount)}
						</div>
					</div>
					{/* <div className="row mb">
						{pharmacy && (
							<div className="pharmacy col-md-4">
								<strong>Pharmacy:</strong>
								<a
									href="#"
									style={{ textDecoration: 'underline dotted', display: 'inline-block' }}
									className="pharmacy-name"
									data-tip
									data-for="pharmacy-name"
								>{pharmacy.name}</a>
								<ReactTooltip place="right" type="light" effect="solid" id="pharmacy-name">
									<div><strong>{pharmacy.name}</strong></div>
									<div>{pharmacy.address1}</div>
									{pharmacy.address2 && (<div>${pharmacy.address1}</div>)}
									<div>{pharmacy.city} {pharmacy.state} {pharmacy.zip}</div>
									<div><abbr title="Phone">P:</abbr> {pharmacy.phone}</div>
									<div><abbr title="Fax">F:</abbr> {pharmacy.fax}</div>
								</ReactTooltip>
							</div>
						)}
						<div className="col-md-4">
							<strong>Care Team: </strong>
							{careteam.length > 0 && (
								<React.Fragment>
									<a
										href="#"
										style={{ textDecoration: 'underline dotted', display: 'inline-block' }}
										className="care-team"
										data-tip
										data-for="care-team"
									>View ({careteam.length})</a>
									<ReactTooltip place="right" type="light" effect="solid" id="care-team">
										{careteam.map(teamMember => (
											<div>{teamMember.isPrimary ? '<strong>' : ''}{teamMember.firstName} {teamMember.lastName}{teamMember.isPrimary ? '</strong>' : ''}</div>
										))}
									</ReactTooltip>
								</React.Fragment>
							)}
							{careteam.length === 0 && 'None'}
						</div>
						{(!isDependentEvisit && insurance) && (
							<div className="col-md-4">
								<strong>Insurance:{' '}</strong>
								<a
									href="#"
									style={{ textDecoration: 'underline dotted', display: 'inline-block' }}
									className="insurance-payer_name"
									data-tip
									data-for="insurance-payer-name"
								>{insurance.payer_name}</a>
								<ReactTooltip place="right" type="light" effect="solid" id="insurance-payer-name">
									<div><strong>Payer: </strong> {insurance.payer_name}</div>
									<div><strong>Member ID:</strong> {insurance.member_id}</div>
								</ReactTooltip>
							</div>
						)}
					</div> */}
				</div>
			</div>
			<div className="row bg-info-light">
				<div className="col-md-9">
					<div className="row pl-xl pt pb">
						<div className="col-md-7">
							<strong>Reason for Visit:</strong>{' '}
							{evisit.provider_visit_type.visit_type}
						</div>
						<div className="col-md-5">
							<strong>Onset Date:</strong>{' '}
							{!isEmpty(evisit.onset_dttm) && short(evisit.onset_dttm)}
						</div>
					</div>
					<div className="row pl-xl pb">
						<div className="col-md-12">
							<strong>Patient Comments:</strong> {evisit.comments}
						</div>
					</div>
					{!myPatient && (
						<div className="row pl-xl pb">
							<div className="col-md-12">
								<strong>Patient of:</strong> {docName}
							</div>
						</div>
					)}
					{referral && (
						<div className="row pl-xl pb referral">
							<div className="col-md-6">
								<strong>Referral From:</strong>&nbsp;
								<a
									href="#"
									style={{
										textDecoration: 'underline dotted',
										display: 'inline-block',
									}}
									className="referral-data"
									data-tip
									data-for="referral-data"
								>
									{referral['closedBy-provider'].name_first}{' '}
									{referral['closedBy-provider'].name_last}
								</a>
								<ReactTooltip
									place="right"
									type="light"
									effect="solid"
									id="referral-data"
								>
									<div>
										<strong>
											{referral['closedBy-provider'].name_first}{' '}
											{referral['closedBy-provider'].name_last}
										</strong>
									</div>
									<div>
										{
											referral['closedBy-provider'].provider_practice
												.practice_name
										}
									</div>
									<div>
										<abbr title="Phone">P:</abbr>{' '}
										{referral['closedBy-provider'].provider_practice.phone}
									</div>
									<div>
										<strong>Referral Reason</strong>
									</div>
									<div>{referral.referral_reason}</div>
								</ReactTooltip>
							</div>
						</div>
					)}
				</div>
				<div className="col-md-3 text-right">
					<div className="pt pr-lg pb">
						{!isClosed && !isScheduled && !isOpen && (
							<Button
								primary
								sm
								className="start-evisit mr"
								title="Start Visit"
								onClick={handleConfirmStartVisit}
								disabled={showStartSwal || showCancelSwal}
							/>
						)}
						{isOpen && !isClosed && (
							<React.Fragment>
								{/* {canRx && (
									<button className="btn btn-primary btn-sm start-rx">Start Rx</button>
								)} */}
								<DropdownButton
									sm
									danger
									title="End Visit"
									items={[
										{ title: 'End Visit', onClick: handleConfirmEndVisit },
										{
											title: 'Refer Patient & End Visit',
											onClick: onReferVisit,
										},
									]}
									right
									containerClassName="end-visit-button-container"
									disabled={isUpdating}
								/>
							</React.Fragment>
						)}
						{isCancelable && (
							<Button
								danger
								sm
								className="cancel-evisit"
								title="Cancel Visit"
								onClick={handleConfirmCancelVisit}
								disabled={showStartSwal || showCancelSwal || isUpdating}
							/>
						)}
						{isClosed && (
							<Link
								to={`/print/discharge-summary/${evisit.id}`}
								className="btn btn-primary btn-sm"
							>
								Print Discharge Summary
							</Link>
						)}
						{!isClosed && (
							<div className="pt">
								<Button
									title="Change Fee"
									className="change-fee"
									sm
									onClick={onChangeFee}
									disabled={showStartSwal || showCancelSwal || isUpdating}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

PatientBar.propTypes = {
	user: PropTypes.object.isRequired,
	evisit: PropTypes.object.isRequired,
	isPatientConnected: PropTypes.bool,
	onReinvitePatient: PropTypes.func.isRequired,
	onStartVisit: PropTypes.func.isRequired,
	onChangeFee: PropTypes.func.isRequired,
	onCancelVisit: PropTypes.func.isRequired,
	onEndVisit: PropTypes.func.isRequired,
	onReferVisit: PropTypes.func.isRequired,
	isDependentEvisit: PropTypes.bool,
	isUpdating: PropTypes.bool,
};

export default PatientBar;
